import type { Node as PMNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { markSystemTr } from './shared';
import { setAttrsAtPos } from '../extensions/listUtils';

type TPatch = { pos: number; attrs: Record<string, unknown> };

/**
 * Ensure nested lists inherit their parent's `listPattern`. Only scans the
 * subtree around the changed region instead of the full document.
 */
const getListPatternPlugin = (listTypeName: string, pluginKeyName: string) => {
  const pluginKey = new PluginKey(pluginKeyName);

  return new Plugin({
    key: pluginKey,
    appendTransaction(_trs, oldState, newState) {
      if (oldState.doc.eq(newState.doc)) return null;

      const listType = newState.schema.nodes[listTypeName];
      if (!listType) return null;

      // Find the changed region
      const diffStart = oldState.doc.content.findDiffStart(newState.doc.content);
      if (diffStart == null) return null;

      // Find the nearest enclosing list that contains the change.
      // We only need to normalize this list subtree, not the entire document.
      const $pos = newState.doc.resolve(diffStart);
      let listPos: number | null = null;
      let listNode: PMNode | null = null;
      for (let d = $pos.depth; d >= 0; d--) {
        if ($pos.node(d).type === listType) {
          listNode = $pos.node(d);
          listPos = $pos.before(d);
          break; // nearest ancestor, not outermost
        }
      }

      // If no enclosing list found, the change might be a freshly pasted/inserted
      // top-level list. Check the node at diffStart itself.
      if (listNode == null) {
        const nodeAt = newState.doc.nodeAt(diffStart);
        if (nodeAt?.type === listType) {
          listNode = nodeAt;
          listPos = diffStart;
        } else {
          return null;
        }
      }

      // Normalize: ensure descendant lists in this subtree match the parent pattern.
      const parentPattern = listNode.attrs.listPattern as string | null;
      const patches: TPatch[] = [];

      listNode.descendants((child, childOffset) => {
        if (child.type !== listType) return true;
        if (child.attrs.listPattern !== parentPattern) {
          patches.push({ pos: listPos! + 1 + childOffset, attrs: { listPattern: parentPattern } });
        }
        return true;
      });

      if (!patches.length) return null;

      const tr = newState.tr.setMeta(pluginKey, 'normalize');
      patches.sort((a, b) => b.pos - a.pos);
      patches.forEach(({ pos, attrs }) => setAttrsAtPos(tr, pos, listType, attrs));
      return tr.steps.length ? markSystemTr(tr) : null;
    },
  });
};

export default getListPatternPlugin;
